/**
 * InstancedMeshPool — manages THREE.InstancedMesh instances grouped by geometry+material.
 *
 * Entities with identical geometry+material are batched into InstancedMesh.
 * Supports add/remove with swap-and-pop compaction.
 */

import * as THREE from 'three/webgpu';
import { Logger } from '../../core/Logger.js';
import { pinStaticMotionVectors } from './staticMotionVectors.js';

interface InstanceSlot {
  entity: number;
  index: number;
}

interface InstanceBatch {
  mesh: THREE.InstancedMesh;
  slots: InstanceSlot[];
  entityToSlot: Map<number, number>; // entity → slot index
  capacity: number;
  /**
   * Rows touched since the last consumed upload, as one evolving [min,max]
   * span (-1 = none). Streaming assembly appends a few rows per frame; with a
   * bare `needsUpdate` three re-uploads the ENTIRE instance buffer
   * (capacity × 64 B) for every touched batch every frame — profiled as
   * multi-second bufferData/bufferSubData bursts during area streaming. A
   * single update range narrows the upload to just the touched rows.
   */
  pendingMin: number;
  pendingMax: number;
  /** Same span for instanceColor rows (3 floats each). */
  pendingColorMin: number;
  pendingColorMax: number;
}

export class InstancedMeshPool {
  private batches = new Map<string, InstanceBatch>();
  private entityToBatchKey = new Map<number, string>();
  private _dirtyBatches = new Set<string>();
  private scene: THREE.Scene;
  private maxPerBatch: number;
  private _swapMatrix = new THREE.Matrix4();
  private _swapColor = new THREE.Color();
  /** True while a post-render range-clear is scheduled (one rAF per burst frame). */
  private _clearQueued = false;

  /** Owner label stamped onto every batch mesh's `name` (`inst:<label>`). */
  private label: string;

  constructor(scene: THREE.Scene, maxPerBatch = 1024, label = '') {
    this.scene = scene;
    this.maxPerBatch = maxPerBatch;
    this.label = label;
  }

  /**
   * Record a touched matrix row and narrow this frame's GPU upload to the
   * touched span via `addUpdateRange`.
   *
   * Range lifecycle: three's InstanceNode copies `instanceMatrix.updateRanges`
   * into its backing buffer every frame but never clears the source list, so
   * stale ranges accumulate and re-upload forever unless WE clear them. The
   * clear is deferred to a rAF: the engine loop re-registers its own rAF at
   * the START of its callback, so any rAF we register DURING that callback
   * (all mutations happen inside engine systems) fires AFTER the frame that
   * consumed the ranges rendered. Environments without rAF (unit tests, SSR)
   * skip ranges entirely — an empty range list means a full upload, which is
   * exactly the pre-optimization behavior.
   */
  private _markRows(batch: InstanceBatch, row: number, colorRow = -1): void {
    const attr = batch.mesh.instanceMatrix;
    if (typeof requestAnimationFrame === 'function') {
      batch.pendingMin = batch.pendingMin < 0 ? row : Math.min(batch.pendingMin, row);
      batch.pendingMax = Math.max(batch.pendingMax, row);
      // ONE evolving span per batch per frame — rewrite it in place rather than
      // accumulating per-row entries (assembly appends are contiguous anyway).
      attr.clearUpdateRanges();
      attr.addUpdateRange(batch.pendingMin * 16, (batch.pendingMax - batch.pendingMin + 1) * 16);
      if (colorRow >= 0 && batch.mesh.instanceColor) {
        batch.pendingColorMin = batch.pendingColorMin < 0 ? colorRow : Math.min(batch.pendingColorMin, colorRow);
        batch.pendingColorMax = Math.max(batch.pendingColorMax, colorRow);
        batch.mesh.instanceColor.clearUpdateRanges();
        batch.mesh.instanceColor.addUpdateRange(batch.pendingColorMin * 3, (batch.pendingColorMax - batch.pendingColorMin + 1) * 3);
      }
      if (!this._clearQueued) {
        this._clearQueued = true;
        requestAnimationFrame(() => {
          this._clearQueued = false;
          for (const b of this.batches.values()) {
            if (b.pendingMin >= 0) { b.mesh.instanceMatrix.clearUpdateRanges(); b.pendingMin = -1; b.pendingMax = -1; }
            if (b.pendingColorMin >= 0 && b.mesh.instanceColor) { b.mesh.instanceColor.clearUpdateRanges(); b.pendingColorMin = -1; b.pendingColorMax = -1; }
          }
        });
      }
    }
    attr.needsUpdate = true;
    if (colorRow >= 0 && batch.mesh.instanceColor) batch.mesh.instanceColor.needsUpdate = true;
  }

  /**
   * Generate a batch key from geometry + material.
   */
  private _key(geometry: THREE.BufferGeometry, material: THREE.Material): string {
    return `${geometry.uuid}:${material.uuid}`;
  }

  /**
   * Add an entity instance.
   * Returns the slot index within the batch.
   */
  add(
    entity: number,
    geometry: THREE.BufferGeometry,
    material: THREE.Material,
    matrix: THREE.Matrix4,
  ): number {
    const key = this._key(geometry, material);
    let batch = this.batches.get(key);

    if (!batch) {
      // Static instances: pin the previous position to the current one (three
      // r185 freezes the GPU copy of the previous instance matrices above the
      // uniform-buffer limit — motion blur streaked every late-filled slot).
      pinStaticMotionVectors(material);
      const mesh = new THREE.InstancedMesh(geometry, material, this.maxPerBatch);
      mesh.count = 0;
      mesh.frustumCulled = false; // We do our own culling
      // Owner attribution: scene censuses group drawables by name, and a pool
      // with no label renders every consumer's batches as one indistinguishable
      // "unnamed" pile (a 232M-triangle group could not be attributed to its
      // spawner in the per-tier probes).
      if (this.label) mesh.name = `inst:${this.label}`;
      this.scene.add(mesh);

      batch = {
        mesh,
        slots: [],
        entityToSlot: new Map(),
        capacity: this.maxPerBatch,
        pendingMin: -1, pendingMax: -1, pendingColorMin: -1, pendingColorMax: -1,
      };
      this.batches.set(key, batch);
    }

    if (batch.slots.length >= batch.capacity) {
      Logger.warn('InstancedMeshPool', `Batch ${key} at capacity (${batch.capacity})`);
      return -1;
    }

    const index = batch.slots.length;
    batch.mesh.setMatrixAt(index, matrix);
    batch.slots.push({ entity, index });
    batch.entityToSlot.set(entity, index);
    batch.mesh.count = batch.slots.length;
    this._markRows(batch, index);

    this.entityToBatchKey.set(entity, key);
    return index;
  }

  /**
   * Remove an entity. Uses swap-and-pop for O(1) removal.
   */
  remove(entity: number): boolean {
    const key = this.entityToBatchKey.get(entity);
    if (!key) return false;

    const batch = this.batches.get(key);
    if (!batch) return false;

    const slotIdx = batch.entityToSlot.get(entity);
    if (slotIdx === undefined) return false;

    const lastIdx = batch.slots.length - 1;

    if (slotIdx !== lastIdx) {
      // Swap with last — must swap ALL per-instance attributes
      const lastSlot = batch.slots[lastIdx];
      batch.mesh.getMatrixAt(lastIdx, this._swapMatrix);
      batch.mesh.setMatrixAt(slotIdx, this._swapMatrix);

      // Swap per-instance color if the batch uses it
      const hasColor = !!batch.mesh.instanceColor;
      if (hasColor) {
        batch.mesh.getColorAt(lastIdx, this._swapColor);
        batch.mesh.setColorAt(slotIdx, this._swapColor);
      }
      // Custom per-instance attributes on the batch geometry (e.g. a crossfade
      // value) ride the same swap-with-last compaction, or the moved instance
      // would inherit the removed one's row.
      this._swapInstancedRows(batch, lastIdx, slotIdx);
      this._markRows(batch, slotIdx, hasColor ? slotIdx : -1);

      lastSlot.index = slotIdx;
      batch.slots[slotIdx] = lastSlot;
      batch.entityToSlot.set(lastSlot.entity, slotIdx);
    }

    batch.slots.pop();
    batch.entityToSlot.delete(entity);
    batch.mesh.count = batch.slots.length;

    this.entityToBatchKey.delete(entity);

    // Remove empty batches — don't dispose geometry/material as they are
    // caller-owned (passed to add()). Only dispose the InstancedMesh itself.
    if (batch.slots.length === 0) {
      this.scene.remove(batch.mesh);
      batch.mesh.dispose();
      this.batches.delete(key);
      this._dirtyBatches.delete(key);
    }

    return true;
  }

  /**
   * The batch mesh backing a (geometry, material) pair, if that batch exists.
   * Lets callers pre-compile ONE newborn batch (`renderer.compileAsync(mesh,
   * camera, scene)`) instead of sweeping the whole scene — a whole-scene
   * compile pays an O(scene) walk per call, which live-profiling (2026-07-31)
   * showed dominating the warm cost in large worlds.
   */
  meshFor(geometry: THREE.BufferGeometry, material: THREE.Material): THREE.InstancedMesh | null {
    return this.batches.get(this._key(geometry, material))?.mesh ?? null;
  }

  /**
   * Every batch mesh this pool owns, for callers that need to apply a setting
   * across all of them (shadow flags, layers, render order).
   */
  *meshes(): IterableIterator<THREE.InstancedMesh> {
    for (const batch of this.batches.values()) yield batch.mesh;
  }

  /** The batch mesh + slot index an entity currently occupies (null when not pooled). */
  slotOf(entity: number): { mesh: THREE.InstancedMesh; index: number } | null {
    const key = this.entityToBatchKey.get(entity);
    const batch = key ? this.batches.get(key) : undefined;
    const index = batch?.entityToSlot.get(entity);
    return batch && index !== undefined ? { mesh: batch.mesh, index } : null;
  }

  /**
   * Write one entity's row of a custom per-instance attribute on its batch
   * geometry (an `InstancedBufferAttribute` the caller added to the geometry
   * handed to `add`), marking ONLY that row for upload. Returns false when the
   * entity is not pooled or the geometry has no such attribute.
   */
  setInstanceAttribute(entity: number, name: string, values: ArrayLike<number>): boolean {
    const slot = this.slotOf(entity);
    if (!slot) return false;
    const attr = slot.mesh.geometry.getAttribute(name) as THREE.InstancedBufferAttribute | undefined;
    if (!attr || !(attr as { isInstancedBufferAttribute?: boolean }).isInstancedBufferAttribute) return false;
    const n = attr.itemSize;
    for (let k = 0; k < n; k++) attr.array[slot.index * n + k] = values[k] ?? 0;
    this._markAttributeRow(attr, slot.index);
    return true;
  }

  private _swapInstancedRows(batch: InstanceBatch, from: number, to: number): void {
    const attrs = batch.mesh.geometry.attributes as Record<string, THREE.BufferAttribute>;
    for (const name of Object.keys(attrs)) {
      const attr = attrs[name] as THREE.InstancedBufferAttribute;
      if (!(attr as { isInstancedBufferAttribute?: boolean }).isInstancedBufferAttribute) continue;
      const n = attr.itemSize;
      for (let k = 0; k < n; k++) attr.array[to * n + k] = attr.array[from * n + k];
      this._markAttributeRow(attr, to);
    }
  }

  /** Per-row update range on a custom instanced attribute, cleared on the next frame like the built-ins. */
  private readonly _rangedAttrs = new Set<THREE.BufferAttribute>();
  private _markAttributeRow(attr: THREE.InstancedBufferAttribute, row: number): void {
    const n = attr.itemSize;
    if (typeof requestAnimationFrame === 'function') {
      // Coalesce with any range already pending this frame: one span covers both.
      const r = attr.updateRanges[0];
      const lo = r ? Math.min(r.start, row * n) : row * n;
      const hi = r ? Math.max(r.start + r.count, (row + 1) * n) : (row + 1) * n;
      attr.clearUpdateRanges();
      attr.addUpdateRange(lo, hi - lo);
      if (!this._rangedAttrs.has(attr)) {
        this._rangedAttrs.add(attr);
        requestAnimationFrame(() => { attr.clearUpdateRanges(); this._rangedAttrs.delete(attr); });
      }
    }
    attr.needsUpdate = true;
  }

  /**
   * Update an entity's transform matrix.
   */
  updateMatrix(entity: number, matrix: THREE.Matrix4): boolean {
    const key = this.entityToBatchKey.get(entity);
    if (!key) return false;

    const batch = this.batches.get(key);
    if (!batch) return false;

    const slotIdx = batch.entityToSlot.get(entity);
    if (slotIdx === undefined) return false;

    batch.mesh.setMatrixAt(slotIdx, matrix);
    this._markRows(batch, slotIdx);
    return true;
  }

  /**
   * Retained for API compatibility. Uploads are now armed per-mutation with
   * narrow update ranges (`_markRows`) — repeated `needsUpdate` sets before a
   * render coalesce into one upload via the attribute version, so a separate
   * flush step no longer gates anything.
   */
  flush(): void {
    this._dirtyBatches.clear();
  }

  /**
   * Get total instance count across all batches.
   */
  get totalInstances(): number {
    let total = 0;
    for (const batch of this.batches.values()) {
      total += batch.slots.length;
    }
    return total;
  }

  /**
   * Get number of batches.
   */
  get batchCount(): number {
    return this.batches.size;
  }

  dispose(): void {
    for (const batch of this.batches.values()) {
      this.scene.remove(batch.mesh);
      // Don't dispose geometry/material — they are caller-owned (passed to add()).
      batch.mesh.dispose();
    }
    this.batches.clear();
    this.entityToBatchKey.clear();
  }
}
